import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/dates'
import { formatNome } from '@/utils/format'
import NoData from '@/assets/no-data.svg?react'
import { useCurrentUser } from '@/features/auth/state/auth'
import type { Alert, AlertHistoryItem } from '@/types/alert'
import { useFetchIdTenantsPermissions } from '@/lib/hooks/tenant'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface AlertListProps {
  alerts: Alert[]
  markAsRead: (id: string) => void
  markAsResolved: (id: string, message?: string, targetUserId?: string) => void
  markAsContested: (id: string, message?: string, targetUserId?: string) => void
  onNoAlerts?: () => void
}

const actionLabels: Record<AlertHistoryItem['action'], string> = {
  CREATED: 'Criado',
  READ: 'Lido',
  RESOLVED: 'Resolvido',
  CONTESTED: 'Contestado',
  CONTACT_ADMIN: 'Contato com admin',
  FORCE_RESOLVED: 'Resolvido pelo admin'
}

const AlertList: React.FC<AlertListProps> = ({ alerts, markAsRead, markAsResolved, markAsContested, onNoAlerts }) => {
  const user = useCurrentUser()
  const isAdminCeo = user?.typeUser === 'ADMIN' && user?.typeCargo === 'CEO'
  const isGlobalAdminCeo = isAdminCeo && user?.id_tenant === '50734366000162'
  const { data: activeTenants = [] } = useFetchIdTenantsPermissions(user?.typeUser)
  const [selectedTab, setSelectedTab] = useState('naoVistos')
  const [selectedTenantTab, setSelectedTenantTab] = useState<string>('all')
  const [dateSortOrder, setDateSortOrder] = useState<'desc' | 'asc'>('desc')
  const [showDialog, setShowDialog] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)
  const [showResolveDialog, setShowResolveDialog] = useState(false)
  const [showContestDialog, setShowContestDialog] = useState(false)
  const [resolutionMessage, setResolutionMessage] = useState('')
  const [contestMessage, setContestMessage] = useState('')

  const visibleAlerts = useMemo(() => {
    if (!isGlobalAdminCeo || selectedTenantTab === 'all') return alerts
    return alerts.filter(alert => String(alert.tenantId ?? '') === selectedTenantTab)
  }, [alerts, isGlobalAdminCeo, selectedTenantTab])

  const filteredAlerts = useMemo(
    () =>
      visibleAlerts.filter(alert =>
        selectedTab === 'naoVistos'
          ? !alert.read && !alert.resolved
          : selectedTab === 'vistos'
            ? alert.read && !alert.resolved
            : alert.resolved
      ),
    [visibleAlerts, selectedTab]
  )

  const sectionedAlerts = useMemo(() => {
    const getAlertTime = (alert: Alert) => new Date(alert.date).getTime()

    const sections = filteredAlerts.reduce<Record<string, { title: string; alerts: Alert[] }>>((acc, alert) => {
      const sectionKey = isAdminCeo
        ? `${alert.tenantId ?? 'sem-tenant'}::${alert.tenantName || 'Tenant não identificado'}`
        : 'default'
      const sectionTitle = isAdminCeo ? alert.tenantName || 'Tenant não identificado' : ''

      if (!acc[sectionKey]) {
        acc[sectionKey] = { title: sectionTitle, alerts: [] }
      }

      acc[sectionKey].alerts.push(alert)
      return acc
    }, {})

    return Object.entries(sections)
      .map(([key, value]) => ({
        key,
        title: value.title,
        companies: Object.entries(
          value.alerts.reduce<Record<string, Alert[]>>((companies, alert) => {
            const companyKey = alert.company || 'Empresa não identificada'
            companies[companyKey] = companies[companyKey] ?? []
            companies[companyKey].push(alert)
            return companies
          }, {})
        )
          .map(([companyName, alerts]) => {
            const sortedCompanyAlerts = [...alerts].sort((left, right) =>
              dateSortOrder === 'asc' ? getAlertTime(left) - getAlertTime(right) : getAlertTime(right) - getAlertTime(left)
            )

            return {
              companyName,
              alerts: sortedCompanyAlerts,
              sortTime:
                dateSortOrder === 'asc'
                  ? Math.min(...sortedCompanyAlerts.map(getAlertTime))
                  : Math.max(...sortedCompanyAlerts.map(getAlertTime))
            }
          })
          .sort((left, right) => {
            if (left.sortTime !== right.sortTime) {
              return dateSortOrder === 'asc' ? left.sortTime - right.sortTime : right.sortTime - left.sortTime
            }

            return left.companyName.localeCompare(right.companyName, 'pt-BR')
          })
      }))
      .sort((left, right) => left.title.localeCompare(right.title, 'pt-BR'))
  }, [filteredAlerts, dateSortOrder, isAdminCeo])

  useEffect(() => {
    if (filteredAlerts.length === 0 && selectedTab === 'naoVistos' && onNoAlerts) {
      onNoAlerts()
    }
  }, [filteredAlerts, selectedTab, onNoAlerts])

  const openDialog = (alert: Alert) => {
    setSelectedAlert(alert)
    setShowDialog(true)
  }

  const openResolveDialog = (alert: Alert) => {
    setSelectedAlert(alert)
    setResolutionMessage(alert.resolutionMessage ?? '')
    setShowResolveDialog(true)
  }

  const openContestDialog = (alert: Alert) => {
    setSelectedAlert(alert)
    setContestMessage(alert.lastAdminMessage ?? '')
    setShowContestDialog(true)
  }

  const closeDialog = () => {
    setShowDialog(false)
    setSelectedAlert(null)
  }

  const handleResolve = () => {
    if (!selectedAlert) return
    markAsResolved(selectedAlert.id, resolutionMessage.trim() || undefined, selectedAlert.referenceUserId || undefined)
    setShowResolveDialog(false)
    setResolutionMessage('')
  }

  const handleContest = () => {
    if (!selectedAlert) return
    markAsContested(selectedAlert.id, contestMessage.trim() || undefined, selectedAlert.referenceUserId || undefined)
    setShowContestDialog(false)
    setContestMessage('')
  }

  return (
    <div>
      <div className="mb-4 flex border-b bg-background">
        <button
          className={`px-4 py-2 text-foreground transition ${
            selectedTab === 'naoVistos' ? 'border-b-2 font-semibold' : 'hover:[var(--hover-color)]'
          }`}
          onClick={() => setSelectedTab('naoVistos')}
        >
          Não Vistos
        </button>
        <button
          className={`px-4 py-2 text-foreground transition ${
            selectedTab === 'vistos' ? 'border-b-2 font-semibold' : 'hover:[var(--hover-color)]'
          }`}
          onClick={() => setSelectedTab('vistos')}
        >
          Vistos
        </button>
        <button
          className={`px-4 py-2 text-foreground transition ${
            selectedTab === 'resolvidos' ? 'border-b-2 font-semibold' : 'hover:[var(--hover-color)]'
          }`}
          onClick={() => setSelectedTab('resolvidos')}
        >
          Resolvidos
        </button>
      </div>

      {isGlobalAdminCeo && activeTenants.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant={selectedTenantTab === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTenantTab('all')}>
            Todos
          </Button>
          {activeTenants.map((tenant: { id_tenant: string; razao_social?: string }) => (
            <Button
              key={tenant.id_tenant}
              variant={selectedTenantTab === tenant.id_tenant ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedTenantTab(tenant.id_tenant)}
            >
              {tenant.razao_social || tenant.id_tenant}
            </Button>
          ))}
        </div>
      )}

      <div className="mb-4 flex items-center justify-end">
        <div className="w-full max-w-[240px]">
          <Select value={dateSortOrder} onValueChange={value => setDateSortOrder(value as 'desc' | 'asc')}>
            <SelectTrigger>
              <SelectValue placeholder="Ordenar por data" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Data mais recente</SelectItem>
              <SelectItem value="asc">Data mais antiga</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-6">
        {filteredAlerts.length > 0 ? (
          sectionedAlerts.map(section => (
            <div key={section.key} className="space-y-4">
              {isAdminCeo && <h3 className="text-lg font-semibold text-foreground">{section.title}</h3>}
              {section.companies.map(companyGroup => (
                <div key={`${section.key}-${companyGroup.companyName}`} className="space-y-4">
                  {isAdminCeo && <h4 className="text-base font-medium text-foreground">{companyGroup.companyName}</h4>}
                  {companyGroup.alerts.map(alert => (
                    <div key={alert.id} className="rounded-md border bg-background-secondary p-4 text-foreground transition">
                      <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center">
                        <div>
                          <p className="font-medium text-foreground">Empresa: {alert.company}</p>
                          {isGlobalAdminCeo && alert.tenantName && alert.tenantName !== alert.company && (
                            <p className="text-sm text-muted-foreground">Grupo: {alert.tenantName}</p>
                          )}
                          {alert.originLabel && <p className="text-sm text-muted-foreground">{alert.originLabel}</p>}
                          {alert.originDescription && <p className="text-xs text-muted-foreground">{alert.originDescription}</p>}
                        </div>
                        <p className="text-sm text-foreground">{formatDate(new Date(alert.date))}</p>
                      </div>

                      <p className="mt-2 text-foreground">{alert.message}</p>

                      {isAdminCeo && (
                        <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-muted-foreground md:grid-cols-4">
                          <p>Criado por: {alert.createdByName ? formatNome(alert.createdByName) : 'Sem criador'}</p>
                          <p>Responsável: {alert.responsibleName ? formatNome(alert.responsibleName) : 'Sem responsável'}</p>
                          <p>Analista: {alert.analystName ? formatNome(alert.analystName) : 'Sem analista'}</p>
                          <p>Destinatário: {alert.recipientName ? formatNome(alert.recipientName) : 'Sem destinatário'}</p>
                        </div>
                      )}

                      {alert.lastAdminMessage && !alert.resolved && (
                        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          Última mensagem do admin: {alert.lastAdminMessage}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        {!alert.read && !alert.resolved && (!isAdminCeo || alert.referenceUserId === user?.id) && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => markAsRead(alert.id)}
                            className="bg-primary text-primary-foreground hover:bg-primary-hover"
                          >
                            Marcar como lido
                          </Button>
                        )}

                        {!alert.resolved && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResolveDialog(alert)}
                            className="bg-primary text-primary-foreground hover:bg-primary-hover"
                          >
                            Resolver
                          </Button>
                        )}

                        {alert.canContest && (
                          <Button variant="outline" size="sm" onClick={() => openContestDialog(alert)}>
                            Contestar
                          </Button>
                        )}

                        <Button variant="outline" size="sm" onClick={() => openDialog(alert)}>
                          Ver detalhes
                        </Button>
                      </div>

                      {alert.resolved && alert.resolvedDate && (
                        <p className="mt-3 text-sm text-foreground">Resolvido em: {formatDate(new Date(alert.resolvedDate))}</p>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="mt-8 flex flex-col items-center">
            <NoData width="180" height="fit" />
            <p className="mt-4 text-lg text-foreground">Nenhum alerta encontrado.</p>
          </div>
        )}
      </div>

      {showDialog && selectedAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 text-foreground shadow-lg">
            <h2 className="mb-4 text-lg font-bold">Detalhes do Alerta</h2>
            <p>
              <strong>Empresa:</strong> {selectedAlert.company}
            </p>
            {isGlobalAdminCeo && selectedAlert.tenantName && selectedAlert.tenantName !== selectedAlert.company && (
              <p>
                <strong>Grupo:</strong> {selectedAlert.tenantName}
              </p>
            )}
            {selectedAlert.originLabel && (
              <p>
                <strong>Origem:</strong> {selectedAlert.originLabel}
              </p>
            )}
            {selectedAlert.originDescription && (
              <p>
                <strong>Contexto:</strong> {selectedAlert.originDescription}
              </p>
            )}
            {selectedAlert.productName && (
              <p>
                <strong>Produto:</strong> {selectedAlert.productName}
              </p>
            )}
            {selectedAlert.productStatus && (
              <p>
                <strong>Status do produto:</strong> {selectedAlert.productStatus}
              </p>
            )}
            {isAdminCeo && (
              <>
                <p>
                  <strong>Criado por:</strong> {selectedAlert.createdByName ? formatNome(selectedAlert.createdByName) : 'Sem criador'}
                </p>
                <p>
                  <strong>Responsável:</strong>{' '}
                  {selectedAlert.responsibleName ? formatNome(selectedAlert.responsibleName) : 'Sem responsável'}
                </p>
                <p>
                  <strong>Analista:</strong> {selectedAlert.analystName ? formatNome(selectedAlert.analystName) : 'Sem analista'}
                </p>
                <p>
                  <strong>Destinatário:</strong>{' '}
                  {selectedAlert.recipientName ? formatNome(selectedAlert.recipientName) : 'Sem destinatário'}
                </p>
              </>
            )}
            <p>
              <strong>Mensagem:</strong> {selectedAlert.message}
            </p>
            {selectedAlert.viewedDate && (
              <p>
                <strong>Visto em:</strong> {formatDate(new Date(selectedAlert.viewedDate))}
              </p>
            )}
            {selectedAlert.resolvedDate && (
              <p>
                <strong>Resolvido em:</strong> {formatDate(new Date(selectedAlert.resolvedDate))}
              </p>
            )}
            {selectedAlert.resolutionMessage && (
              <p>
                <strong>Mensagem de resolução:</strong> {selectedAlert.resolutionMessage}
              </p>
            )}
            {selectedAlert.lastAdminMessage && (
              <p>
                <strong>Última mensagem do admin:</strong> {selectedAlert.lastAdminMessage}
              </p>
            )}
            {typeof selectedAlert.contestCount === 'number' && (
              <p>
                <strong>Contestação:</strong> {selectedAlert.contestCount} de 3
              </p>
            )}

            {selectedAlert.history && selectedAlert.history.length > 0 && (
              <div className="mt-4 space-y-3">
                <h3 className="font-semibold">Histórico</h3>
                {selectedAlert.history.map(item => (
                  <div key={item.id} className="rounded-md border p-3">
                    <div className="flex flex-col justify-between gap-1 sm:flex-row">
                      <p className="font-medium">{actionLabels[item.action]}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(new Date(item.createdDate))}</p>
                    </div>
                    {item.actorName && <p className="text-sm text-muted-foreground">Por: {item.actorName}</p>}
                    {item.message && <p className="mt-1 text-sm">{item.message}</p>}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <Button variant="default" size="sm" onClick={closeDialog} className="bg-primary text-primary-foreground hover:bg-primary-hover">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showResolveDialog && selectedAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-bold text-foreground">Escreva a mensagem de resolução</h2>
            <textarea
              value={resolutionMessage}
              onChange={e => setResolutionMessage(e.target.value)}
              className="mb-4 w-full rounded border p-2 text-foreground"
              placeholder="Digite a mensagem de resolução"
            />
            <div className="mt-4 flex justify-end space-x-2">
              <Button onClick={handleResolve} className="bg-primary text-primary-foreground hover:bg-primary-hover">
                Resolver e enviar
              </Button>
              <Button onClick={() => setShowResolveDialog(false)} className="text-gray-500">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showContestDialog && selectedAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-bold text-foreground">Mensagem da contestação</h2>
            <textarea
              value={contestMessage}
              onChange={e => setContestMessage(e.target.value)}
              className="mb-4 w-full rounded border p-2 text-foreground"
              placeholder="Explique por que a resolução foi contestada"
            />
            <div className="mt-4 flex justify-end space-x-2">
              <Button onClick={handleContest} className="bg-primary text-primary-foreground hover:bg-primary-hover">
                Contestar
              </Button>
              <Button onClick={() => setShowContestDialog(false)} className="text-gray-500">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AlertList
